// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/registry.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/shortcuts/src/registry.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo custodia-parental-app
//     node fleet/fleet.ts check --repo custodia-parental-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Reactive registry of commands and bindings.
 *
 * Owns the merge between defaults and user overrides, and exposes accessors
 * for both dispatch and UI (help modal, profile/shortcuts page).
 */

import { batch, createSignal, runWithOwner } from "solid-js";
import { createStore, produce } from "solid-js/store";

import type { Command, CommandHandler, KeyBinding, Platform, UserOverride } from "./types";

interface RegistryState {
  commands: Record<string, Command>;
  // Handlers are kept separately so a feature can register multiple bindings
  // for one command while another feature owns the runtime behaviour.
  handlers: Record<string, CommandHandler | undefined>;
  defaultBindings: KeyBinding[];
  userOverrides: UserOverride[];
}

export interface Registry {
  // ── Commands ─────────────────────────────────────────────────────────────
  registerCommand: (cmd: Command) => () => void;
  setHandler: (commandId: string, handler: CommandHandler) => () => void;
  getCommand: (id: string) => Command | undefined;
  getHandler: (id: string) => CommandHandler | undefined;
  commands: () => Command[];

  // ── Bindings ─────────────────────────────────────────────────────────────
  registerBinding: (binding: KeyBinding) => () => void;
  registerBindings: (bindings: readonly KeyBinding[]) => () => void;
  bindings: () => KeyBinding[];
  bindingsFor: (commandId: string) => KeyBinding[];

  // ── User overrides ───────────────────────────────────────────────────────
  setUserBinding: (commandId: string, keys: string) => void;
  resetUserBinding: (commandId: string) => void;
  userOverrides: () => UserOverride[];
  setUserOverrides: (overrides: readonly UserOverride[]) => void;

  // ── Platform filter ──────────────────────────────────────────────────────
  setPlatform: (platform: Platform) => void;
  platform: () => Platform;
}

export function createRegistry(initialPlatform: Platform): Registry {
  const [state, setState] = createStore<RegistryState>({
    commands: {},
    handlers: {},
    defaultBindings: [],
    userOverrides: [],
  });
  const [platform, setPlatform] = createSignal<Platform>(initialPlatform);

  const registerCommand: Registry["registerCommand"] = (cmd) => {
    runWithOwner(null, () => {
      setState(
        produce((draft) => {
          draft.commands[cmd.id] = cmd;
          if (!draft.handlers[cmd.id] && cmd.run) {
            draft.handlers[cmd.id] = cmd.run;
          }
        }),
      );
    });
    return () => {
      runWithOwner(null, () => {
        setState(
          produce((draft) => {
            // `Reflect.deleteProperty` is the dynamic-delete-safe form; the
            // registry treats absent keys as "command not registered".
            Reflect.deleteProperty(draft.commands, cmd.id);
            Reflect.deleteProperty(draft.handlers, cmd.id);
          }),
        );
      });
    };
  };

  const setHandler: Registry["setHandler"] = (commandId, handler) => {
    const previous = state.handlers[commandId];
    runWithOwner(null, () => {
      setState("handlers", commandId, () => handler);
    });
    // Only restore if our handler is still the current one — avoids
    // resurrecting a stale handler if another consumer registered after us.
    const dispose = () => {
      if (state.handlers[commandId] === handler) {
        runWithOwner(null, () => {
          setState("handlers", commandId, () => previous);
        });
      }
    };
    return dispose;
  };

  const registerBinding: Registry["registerBinding"] = (binding) => {
    const normalized: KeyBinding = { source: "default", ...binding };
    runWithOwner(null, () => {
      setState(
        "defaultBindings",
        produce((draft) => {
          draft.push(normalized);
        }),
      );
    });
    return () => {
      runWithOwner(null, () => {
        setState(
          "defaultBindings",
          produce((draft) => {
            const idx = draft.findIndex(
              (b) =>
                b.command === normalized.command &&
                b.keys === normalized.keys &&
                b.scope === normalized.scope,
            );
            if (idx !== -1) {
              draft.splice(idx, 1);
            }
          }),
        );
      });
    };
  };

  const registerBindings: Registry["registerBindings"] = (bindings) => {
    const normalized: KeyBinding[] = bindings.map((b) => ({ source: "default", ...b }));
    runWithOwner(null, () => {
      setState(
        "defaultBindings",
        produce((draft) => {
          for (const b of normalized) {
            draft.push(b);
          }
        }),
      );
    });
    return () => {
      runWithOwner(null, () => {
        setState(
          "defaultBindings",
          produce((draft) => {
            for (const b of normalized) {
              const idx = draft.findIndex(
                (item) =>
                  item.command === b.command && item.keys === b.keys && item.scope === b.scope,
              );
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          }),
        );
      });
    };
  };

  const bindings: Registry["bindings"] = () => {
    const overrides = new Map(state.userOverrides.map((o) => [o.command, o.keys]));
    const result: KeyBinding[] = [];

    const overriddenCommands = new Set(overrides.keys());

    for (const b of state.defaultBindings) {
      if (overriddenCommands.has(b.command)) {
        // skip default; user overrode this command
        continue;
      }
      result.push(b);
    }
    for (const [command, keys] of overrides) {
      if (keys === "") {
        // Empty string means "disabled" — neither default nor user binding fires.
        continue;
      }
      const defaultBinding = state.defaultBindings.find((b) => b.command === command);
      result.push({
        command,
        keys,
        scope: defaultBinding?.scope,
        when: defaultBinding?.when,
        priority: defaultBinding?.priority,
        platforms: defaultBinding?.platforms,
        source: "user",
      });
    }
    return result;
  };

  const bindingsFor: Registry["bindingsFor"] = (commandId) =>
    bindings().filter((b) => b.command === commandId);

  const setUserBinding: Registry["setUserBinding"] = (commandId, keys) => {
    runWithOwner(null, () => {
      setState(
        "userOverrides",
        produce((draft) => {
          const idx = draft.findIndex((o) => o.command === commandId);
          if (idx === -1) {
            draft.push({ command: commandId, keys });
          } else {
            draft[idx] = { command: commandId, keys };
          }
        }),
      );
    });
  };

  const resetUserBinding: Registry["resetUserBinding"] = (commandId) => {
    runWithOwner(null, () => {
      setState(
        "userOverrides",
        produce((draft) => {
          const idx = draft.findIndex((o) => o.command === commandId);
          if (idx !== -1) {
            draft.splice(idx, 1);
          }
        }),
      );
    });
  };

  const setUserOverrides: Registry["setUserOverrides"] = (overrides) => {
    batch(() => {
      runWithOwner(null, () => {
        setState("userOverrides", [...overrides]);
      });
    });
  };

  return {
    registerCommand,
    setHandler,
    getCommand: (id) => state.commands[id],
    getHandler: (id) => state.handlers[id],
    commands: () => Object.values(state.commands),
    registerBinding,
    registerBindings,
    bindings,
    bindingsFor,
    setUserBinding,
    resetUserBinding,
    userOverrides: () => state.userOverrides,
    setUserOverrides,
    setPlatform: (p) => {
      setPlatform(p);
    },
    platform,
  };
}
